import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faCrown, faHome } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import type { UseFiltersPayload } from '@domain-group/react-hooks'

import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import PageContext from '@contexts/pageContext'

import useAgencySelector from '@hooks/useAgencySelector'
import useCampaign from '@hooks/useCampaign'
import useCampaigns from '@hooks/useCampaigns'
import useCurrentUser from '@hooks/useCurrentUser'
import useSelector from '@hooks/useSelector'

import defaultRequestOptions from '@sections/Client/packs/Overviews/defaultRequestOptions'

import CampaignListItem from '../CampaignListItem'
import CampaignsBlankState from '../CampaignsBlankState'

const buildPageFilterFields = () => {
  const boxProps = { marginBottom: 'large' }
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Address',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'users',
        entityTitleKey: 'full_name',
        label: 'Agent',
        searchRequestOptions: {
          actable_types: ['Agent'],
        },
      },
      key: 'userId',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All Statuses',
        inputComponent: 'select',
        style,
      },
      key: 'campaign_status',
      label: 'Filter Status',
      values: [
        { key: 'current', label: 'Current' },
        { key: 'leased', label: 'Leased' },
        { key: 'offmarket', label: 'Off market' },
        { key: 'proposal', label: 'Proposal' },
        { key: 'sold', label: 'Sold' },
        { key: 'withdrawn', label: 'Withdrawn' },
        { key: 'archived', label: 'Archived' },
      ],
    },
    {
      callbacks: {
        buildSearchRequestOptions: () => ({ actable_types: ['OfficeAdministrator'] }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        label: 'Priority For',
        entityKey: 'users',
        entityTitleKey: 'full_name',
      },
      key: 'priority_campaign_user_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'Please Select...',
        inputComponent: 'select',
        style,
      },
      key: 'published',
      label: 'Published',
      values: [
        { key: 'true', label: 'Published' },
        { key: 'false', label: 'Not Published' },
      ],
    },
  ]
}

interface PageContentProps {
  filterPayload: UseFiltersPayload,
}

const PageContent = (props: PageContentProps) => {
  const { filterPayload } = props
  const { pageFilters, persistedFilters } = filterPayload

  // @ts-expect-error TS2339
  const { priority_campaign_user_id: priorityCampaignUserId, userId } = pageFilters || {}

  const users = useSelector(reduxState => reduxState.entities.users)

  const { agencyIdParam, selectedAgencyId } = useAgencySelector()

  const { currentUser, isAgent, isEbuAdmin } = useCurrentUser()

  const selectedPriorityUserId = priorityCampaignUserId || currentUser.id

  useLocation()

  // @ts-expect-error TS2339
  const { type }: { redirect?: string } = parsePermittedQueryParams(getQueryParamsFromHash(), ['type'])
  const marketingType = type || 'property'

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters, updateFilters,
    },
    defaultFilters,
  } = pageContext

  const campaignsPayload = useCampaigns({
    filters: {
      agency_id: isAgent || isEbuAdmin ? undefined : selectedAgencyId,
      agency_marketing: (marketingType === 'agency').toString() as 'true' | 'false',
      agent_id: userId ? users[userId]?.actable_id : undefined,
      order_by: 'campaigns.updated_at desc',
      sort_by_priority_campaign_for_user_id: !priorityCampaignUserId ? currentUser.id : undefined,
      ...pageFilters,
    },
    performHttpRequests: !!agencyIdParam,
    requestOptions: {
      ...defaultRequestOptions.campaign,
    },
    sortBy: 'cache_key desc',
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCampaigns,
    filteredCampaignsCount,
    hasFilteredCampaigns,
    loading,
  } = campaignsPayload

  const { callbacks: { newCampaignModal } } = useCampaign()

  return (
    <>
      <Helmet>
        <title>Campaigns | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button
            buttonStyle="primaryCreate"
            onClick={() => newCampaignModal({ shouldLaunchModal: true })}
            size="medium"
          >
            Create
          </Button>
      )}
        nestedNavigation
        activeTabBarItemKey={marketingType}
        tabBarItems={[
          {
            icon: faHome,
            key: 'property',
            // @ts-expect-error TS2353
            onClick: () => updateFilters({ type: 'property' }),
            title: 'Property Marketing',
          },
          {
            icon: faCrown,
            key: 'agency',
            // @ts-expect-error TS2353
            onClick: () => updateFilters({ type: 'agency' }),
            title: 'Agency Marketing',
          },
        ]}
        title="Campaigns"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation
              locationKey="agency.agencyDashboard.campaigns.banner"
            />

            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredCampaigns && <CampaignsBlankState />}

            {!loading && hasFilteredCampaigns && (
              <DashboardModule
                contentBoxProps={{ flexDirection: 'column' }}
                title="Campaigns"
              >
                {/*
                 // @ts-expect-error TS2322 */}
                <DashboardModule.ScrollableSection maxHeight={800}>
                  {filteredCampaigns.map(campaign => (
                    <CampaignListItem
                      key={campaign.id}
                      campaign={campaign}
                      selectedPriorityUserId={selectedPriorityUserId}
                    />
                  ))}
                </DashboardModule.ScrollableSection>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredCampaignsCount}
                  loading={loading}
                />
              </DashboardModule>
            )}

          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation
              locationKey="agency.agencyDashboard.campaigns.sidebar"
            />

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              externalFilters={persistedFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
